import { FormControl, FormLabel, FormGroup, Typography, Grid, Tooltip } from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { visuallyHidden } from '@mui/utils'

import TextInput from '../common/TextInput'
import EmailInput from '../common/EmailInput'
import CheckboxInput from '../common/CheckboxInput'
import PhoneNumberInput from '../common/PhoneNumberInput'
import IconButtonWithProgress from '../common/IconButtonWithProgress'

import intl from '../../helper/intl'
import validate from '../../helper/validate'

import useStyles from './IndividualFieldset.styles'
import utils from '../../helper/utils'

const IndividualFieldset = ({
  id,
  className,
  legend,
  autoFocus,
  disabled,
  firstName,
  lastName,
  email,
  emailConfirmed,
  onConfirmEmail,
  countryCode,
  phoneNumber,
  phoneNumberConfirmed,
  onConfirmPhoneNumber,
  phoneNumberSms,
  onChange,
  onDelete,
  showDeleteProgress
}) => {
  const classes = useStyles()

  const onIndividualFieldChange = (paramName, paramValue) => {
    if (typeof onChange === 'function') {
      const individual = { id, firstName, lastName, email, emailConfirmed, countryCode, phoneNumber, phoneNumberConfirmed, phoneNumberSms }
      individual[paramName] = paramValue
      onChange(individual)
    }
  }

  return (
    <FormControl
      className={classes.wrapper + (className ? ' ' + className : '')}
      id={'individual-fieldset-' + id}
      component="fieldset"
      role="group"
    >
      {/* Legend of the fieldset, must be the first child of the fieldset. For screen readers only. */}
      {legend && (
        <Typography style={visuallyHidden} component="legend">
          {legend}
        </Typography>
      )}

      {/* Visible text that will act as the legend of the fieldset. Hidden for screen readers. */}
      {legend && (
        <FormLabel className={classes.legend} component="p" aria-hidden="true" role="presentation">
          {legend}
        </FormLabel>
      )}

      <FormGroup>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextInput
              id={'individual-fieldset-' + id + '--first-name'}
              label={intl.translate('account_individuals_page__first_name')}
              autoFocus={autoFocus}
              disabled={disabled}
              value={firstName}
              onChange={(event) => onIndividualFieldChange('firstName', event.target.value)}
              isValid={(value) => !!value}
              errorMessage={intl.translate('general__empty_field_error')}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextInput
              id={'individual-fieldset-' + id + '--last-name'}
              label={intl.translate('account_individuals_page__last_name')}
              disabled={disabled}
              value={lastName}
              onChange={(event) => onIndividualFieldChange('lastName', event.target.value)}
              isValid={(value) => !!value}
              errorMessage={intl.translate('general__empty_field_error')}
            />
          </Grid>
          <Grid item xs={12}>
            {utils.checkPermission('relatives_email_view') && (
              <>
                <EmailInput
                  id={'individual-fieldset-' + id + '--email'}
                  label={intl.translate('account_individuals_page__email')}
                  disabled={disabled}
                  confirmed={emailConfirmed}
                  onConfirm={onConfirmEmail}
                  value={email}
                  onChange={(event) => onIndividualFieldChange('email', event.target.value)}
                  isValid={(value) => (value ? validate.isValidEmail(value) : true)}
                  errorMessage={intl.translate('general__invalid_email')}
                />
                {!emailConfirmed && <Typography variant="body1" paragraph>{intl.translate('email_input__description')}</Typography>}
              </>
            )}
          </Grid>
          <Grid item xs={12}>
            {utils.checkPermission('relatives_phone_number_view') && (
              <PhoneNumberInput
                id={'individual-fieldset-' + id + '--phone-number'}
                label={intl.translate('account_individuals_page__phone_number')}
                disabled={disabled}
                confirmed={phoneNumberConfirmed}
                onConfirm={onConfirmPhoneNumber}
                value={phoneNumber}
                countryCodeValue={countryCode}
                onPhoneNumberChange={(event) => onIndividualFieldChange('phoneNumber', event.target.value)}
                onCountryCodeChange={(event) => onIndividualFieldChange('countryCode', event.target.value)}
                isValid={(value) => (value ? validate.isValidPhoneNumber(value, { countryCode }) : true)}
              />
            )}
            <CheckboxInput
              id={'phone-fieldset-' + id + '--phone-number-sms'}
              label={intl.translate('general__phone_number_sms')}
              disabled={disabled}
              checked={phoneNumberSms}
              onChange={(event) => onIndividualFieldChange('phoneNumberSms', event.target.checked)}
            />
          </Grid>
        </Grid>
      </FormGroup>

      {onDelete && (
        <Tooltip title={intl.translate('general__delete')}>
          {/* The <div> is necessary to avoid problems with the tooltip wrapping a disabled button */}
          <div className={classes['delete-btn']}>
            <IconButtonWithProgress
              showProgress={showDeleteProgress}
              aria-label={intl.translate('general__delete') + ' ' + legend}
              onClick={onDelete}
            >
              <DeleteIcon />
            </IconButtonWithProgress>
          </div>
        </Tooltip>
      )}
    </FormControl>
  )
}

export default IndividualFieldset
